import { useState } from 'react';
import { Unlink, ShieldAlert, Send, Loader2, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

const JUSTIFICATIVA_MINIMA = 10;

/**
 * Desfazer um vínculo errado (produto do fornecedor → código do ERP).
 * Admin: apaga o vínculo para poder vincular o código correto.
 * Gerente: só o admin desvincula — o modal explica e envia a solicitação ao admin.
 *
 * `vinculo`: { id, fornecedorNome, codigoFornecedor, descricaoFornecedor, codigoErp, descricaoErp }
 */
export default function ModalDesvincular({ vinculo, outroFornecedor = false, onFechar, onDesvinculado, onSolicitado }) {
  const { isAdmin } = useAuth();
  const ehAdmin = isAdmin();
  const [justificativa, setJustificativa] = useState('');
  const [codigoCorreto, setCodigoCorreto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const justificativaOk = justificativa.trim().length >= JUSTIFICATIVA_MINIMA;

  if (!vinculo) return null;

  async function desvincular() {
    setEnviando(true);
    try {
      const resp = await apiService.precificacao.desvincular(vinculo.id);
      toast.success(`Vínculo com o código ${vinculo.codigoErp} desfeito. Agora escolha o código correto.`);
      onDesvinculado?.(resp.data.data);
      onFechar();
    } catch (error) {
      console.error('❌ Erro ao desvincular:', error);
    } finally {
      setEnviando(false);
    }
  }

  async function solicitar() {
    setEnviando(true);
    try {
      await apiService.precificacao.solicitarDesvinculo(vinculo.id, {
        justificativa: justificativa.trim(),
        codigoErpCorreto: codigoCorreto.trim(),
      });
      toast.success('Pedido enviado ao admin. Acompanhe na aba Vínculos.');
      onSolicitado?.();
      onFechar();
    } catch (error) {
      console.error('❌ Erro ao pedir desvínculo:', error);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal isOpen onClose={onFechar} title="Desvincular produto do ERP" size="sm">
      <div className="space-y-4">
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {outroFornecedor ? 'Vínculo de outro fornecedor que será desfeito' : 'Vínculo que será desfeito'}
          </p>
          <p><strong>{vinculo.fornecedorNome || 'Fornecedor'}</strong></p>
          <p>Produto do fornecedor: <span className="font-mono">{vinculo.codigoFornecedor}</span> {vinculo.descricaoFornecedor}</p>
          <p className="flex items-center gap-1">
            <Unlink className="w-4 h-4 text-gray-400" />
            Código no ERP: <span className="font-mono font-semibold">{vinculo.codigoErp}</span> {vinculo.descricaoErp}
          </p>
        </div>

        {ehAdmin ? (
          <>
            <p className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
              <ShieldAlert className="w-5 h-5 flex-shrink-0" />
              O vínculo será apagado e deixa de valer para as próximas notas deste fornecedor.
              Depois, escolha o código correto do ERP para este produto.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onFechar} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button
                type="button"
                disabled={enviando}
                onClick={desvincular}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />} Desvincular
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg p-3">
              <Lock className="w-5 h-5 flex-shrink-0 text-amber-600" />
              <span>
                <strong>Só o admin pode desvincular.</strong> Envie uma solicitação ao admin para fazer o
                desbloqueio. Quando ele aprovar, o vínculo errado é apagado.
              </span>
            </p>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Por que o vínculo está errado? *</span>
              <textarea
                value={justificativa}
                onChange={e => setJustificativa(e.target.value)}
                rows={3}
                placeholder="Ex.: o produto da nota é a versão 220V, mas foi vinculado ao código da 110V"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
              {!justificativaOk && <span className="text-[11px] text-gray-500">Mínimo {JUSTIFICATIVA_MINIMA} caracteres.</span>}
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Código correto no ERP (opcional)</span>
              <input
                value={codigoCorreto}
                onChange={e => setCodigoCorreto(e.target.value)}
                placeholder="Se souber, o admin já vincula o correto ao aprovar"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onFechar} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button
                type="button"
                disabled={enviando || !justificativaOk}
                onClick={solicitar}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Solicitar ao admin
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
